/**
 * Utility functions for query-related operations
 */

/**
 * Finds an expert from the experts list based on query data
 * @param {Array} experts - List of experts
 * @param {Object} query - Query object containing leads and emailSentOut
 * @returns {Object|null} - The found expert or null
 */
export const findExpertFromQuery = (experts, query) => {
  if (!experts || !query) {
    console.log('Missing experts or query:', { hasExperts: !!experts, hasQuery: !!query });
    return null;
  }

  console.log('Query structure:', {
    id: query.id,
    hasLeads: !!query.leads,
    leadsLength: query.leads?.length,
    emailSentOut: query.emailSentOut,
    expertID: query.expertID,
    expertsID: query.expertsID,
  });

  // Try to find expert through emailSentOut lead
  let expert = null;

  // Method 1: Try direct expert assignment (most direct connection)
  // Handle both expertID (legacy) and expertsID (new field) with expertsID taking precedence
  const expertByNewId = query.expertsID ? experts.find((e) => e.id === Number(query.expertsID)) : null;
  const expertByLegacyId = query.expertID ? experts.find((e) => e.id === query.expertID) : null;
  expert = expertByNewId || expertByLegacyId;

  if (expert) {
    console.log('Expert from direct assignment:', expert, 'via', expertByNewId ? 'expertsID' : 'expertID');
  }

  // Method 2: Try through emailSentOut lead
  if (!expert && query.leads && query.emailSentOut) {
    try {
      const matchingLead = query.leads.find((lead) => lead.id === query.emailSentOut);
      console.log('Matching lead:', matchingLead);

      if (matchingLead && matchingLead.expertID) {
        expert = experts.find((e) => e.id === matchingLead.expertID);
        console.log('Expert from matching lead:', expert);
      }
    } catch (error) {
      console.error('Error finding expert through emailSentOut:', error);
    }
  }

  // Method 3: Try the first lead
  if (!expert && query.leads && query.leads.length > 0) {
    try {
      const firstLead = query.leads[0];
      console.log('First lead:', firstLead);

      if (firstLead && firstLead.expertID) {
        expert = experts.find((e) => e.id === firstLead.expertID);
        console.log('Expert from first lead:', expert);
      }
    } catch (error) {
      console.error('Error finding expert through first lead:', error);
    }
  }

  // Method 4: Check if the query has a company property directly
  if (!expert && query.company) {
    // Find any expert from the same company
    expert = experts.find((e) => e.company === query.company);
    console.log('Expert from query company match:', expert);
  }

  return expert || null;
};

/**
 * Checks if a query belongs to a specific company
 * @param {Array} experts - List of experts
 * @param {Object} query - Query object
 * @param {string} companyName - Company name to check against
 * @returns {boolean} - Whether the query belongs to the company
 */
export const queryBelongsToCompany = (experts, query, companyName) => {
  if (!experts || !query || !companyName) {
    console.log('Missing parameters for queryBelongsToCompany');
    return false;
  }

  // Direct check: If query has a company property, check it directly
  if (query.company) {
    const directMatch = query.company === companyName;
    console.log(`Direct company match for query ${query.id}: ${directMatch}`);
    if (directMatch) return true;
  }

  // Try to find the expert associated with this query
  const expert = findExpertFromQuery(experts, query);

  // Check if we found an expert and if their company matches
  if (expert && expert.company === companyName) {
    return true;
  }

  // If we couldn't find an expert or the company doesn't match
  return false;
};
